import { Anchor, Button, Card, Group, Stack, Text } from '@mantine/core';
import { createFileRoute,Link } from '@tanstack/react-router';

import { MarketingHero } from '../components/marketing/MarketingHero';
import { MarketingNextSteps } from '../components/marketing/MarketingNextSteps';
import { MarketingSection } from '../components/marketing/MarketingSection';
import { PublicShell } from '../components/marketing/PublicShell';
import { JoinLink } from '../components/ui/JoinLink';
import { useCmsImage, useCmsSlot } from '../lib/cms';
import { CTA_CLAY } from '../lib/cta-clay';

export const Route = createFileRoute('/our-ethos')({
  component: WhatWeAreAndArentPage,
});

function WhatWeAreAndArentPage() {
  // Every section on this page is CMS-editable: bodies, list items, and (added
  // 2026-07-03) each section's eyebrow / title / lede.
  //
  // Section ORDER: Hero → Definition → Model → Commitment → Note → Read on.
  // The "The promise" and "In contrast" sections were removed 2026-08-18 at
  // Richie's request (feedback y5uYAuKIbZM4aNSh323Lx / W87b8Y3XNcBtRBwqqw5k4 —
  // he had typed "REMOVE THIS SECTION" into their eyebrow + title fields). The
  // slots they read are marked deprecated in the registry, never deleted, since
  // prod still holds real overrides on all of them. The old "In a sentence"
  // taglines card was removed earlier.
  const heroEyebrow = useCmsSlot<'markdown'>('what_we_are_and_arent.hero_eyebrow');
  const heroTitle = useCmsSlot<'markdown'>('what_we_are_and_arent.hero_title');
  const heroLede = useCmsSlot<'markdown'>('what_we_are_and_arent.hero_lede');
  const definition = useCmsSlot<'markdown'>('what_we_are_and_arent.definition');
  const definitionEyebrow = useCmsSlot<'markdown'>('what_we_are_and_arent.definition_eyebrow');
  const definitionTitle = useCmsSlot<'markdown'>('what_we_are_and_arent.definition_title');
  const model = useCmsSlot<'bullet_list'>('what_we_are_and_arent.model');
  const modelEyebrow = useCmsSlot<'markdown'>('what_we_are_and_arent.model_eyebrow');
  const modelTitle = useCmsSlot<'markdown'>('what_we_are_and_arent.model_title');
  const commitment = useCmsSlot<'bullet_list'>('what_we_are_and_arent.commitment');
  const commitmentEyebrow = useCmsSlot<'markdown'>('what_we_are_and_arent.commitment_eyebrow');
  const commitmentTitle = useCmsSlot<'markdown'>('what_we_are_and_arent.commitment_title');
  const commitmentLede = useCmsSlot<'markdown'>('what_we_are_and_arent.commitment_lede');
  const founderNote = useCmsSlot<'markdown'>('what_we_are_and_arent.founder_note');
  const founderEyebrow = useCmsSlot<'markdown'>('what_we_are_and_arent.founder_eyebrow');
  const founderTitle = useCmsSlot<'markdown'>('what_we_are_and_arent.founder_title');
  const founderSignature = useCmsSlot<'markdown'>('what_we_are_and_arent.founder_signature');
  const definitionHeadword = useCmsSlot<'markdown'>('what_we_are_and_arent.definition_headword');
  const definitionPronunciation = useCmsSlot<'markdown'>(
    'what_we_are_and_arent.definition_pronunciation',
  );
  const definitionPartOfSpeech = useCmsSlot<'markdown'>(
    'what_we_are_and_arent.definition_part_of_speech',
  );
  const modelCtaFounding = useCmsSlot<'markdown'>('what_we_are_and_arent.model_cta_founding');
  const modelCtaFaq = useCmsSlot<'markdown'>('what_we_are_and_arent.model_cta_faq');
  const ctaArtistLabel = useCmsSlot<'markdown'>('what_we_are_and_arent.cta_artist');
  const ctaHostLabel = useCmsSlot<'markdown'>('what_we_are_and_arent.cta_host');
  const nextTitle = useCmsSlot<'markdown'>('what_we_are_and_arent.next_title');
  const { image: heroImg, ready: heroImgReady } = useCmsImage('what_we_are_and_arent.hero_image');

  return (
    <PublicShell>
      <MarketingHero
        eyebrow={heroEyebrow}
        title={heroTitle}
        lede={heroLede}
        {...(heroImgReady ? { backgroundImage: heroImg.url } : {})}
        backgroundImageAlt={heroImg.alt}
        backgroundImageOpacity={heroImg.opacity ?? 0.2}
      />

      <MarketingSection eyebrow={definitionEyebrow} title={definitionTitle}>
        <Card
          withBorder
          padding="xl"
          radius="lg"
          style={{
            backgroundColor: 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-dark-6))',
            borderColor: 'var(--mantine-color-brand-2)',
          }}
        >
          <Stack gap="sm">
            <Group gap="sm" align="baseline" wrap="wrap">
              <Text
                component="span"
                style={{
                  fontFamily: 'var(--wwaave-font-display)',
                  fontSize: 'clamp(1.6rem, 3vw, 2.1rem)',
                  fontWeight: 700,
                  letterSpacing: '-0.02em',
                  color: 'var(--mantine-color-ink-9)',
                }}
              >
                {definitionHeadword}
              </Text>
              <Text component="span" c="ink.6" fs="italic" size="lg">
                {definitionPronunciation}
              </Text>
              <Text component="span" c="ink.6" fs="italic" size="lg">
                {definitionPartOfSpeech}
              </Text>
            </Group>
            {/* pre-line so each newline the admin types in the CMS renders as its
                own line (the definition is written as several short lines). */}
            <Text
              size="lg"
              style={{ lineHeight: 1.6, color: 'var(--mantine-color-ink-8)', whiteSpace: 'pre-line' }}
            >
              {definition}
            </Text>
          </Stack>
        </Card>
      </MarketingSection>

      <MarketingSection eyebrow={modelEyebrow} title={modelTitle}>
        <Stack gap="md">
          {model.map((para, i) => (
            <Text key={i} {...(i === 0 ? {} : { c: 'ink.7' })} size="lg" style={{ lineHeight: 1.6 }}>
              {para}
            </Text>
          ))}
          <Group gap="lg" mt="md">
            <Anchor component={Link} to="/founding-venues" fw={500}>
              {modelCtaFounding}
            </Anchor>
            <Anchor component={Link} to="/faq" fw={500}>
              {modelCtaFaq}
            </Anchor>
          </Group>
        </Stack>
      </MarketingSection>

      <MarketingSection eyebrow={commitmentEyebrow} title={commitmentTitle} lede={commitmentLede} tinted>
        <Stack gap="lg">
          {commitment.map((para, i) => (
            <Text key={i} size="lg" style={{ lineHeight: 1.6 }}>
              {para}
            </Text>
          ))}
        </Stack>
      </MarketingSection>

      <MarketingSection eyebrow={founderEyebrow} title={founderTitle}>
        <Card
          withBorder
          padding="xl"
          radius="lg"
          style={{
            backgroundColor: 'light-dark(rgba(255, 255, 255, 0.9), var(--mantine-color-dark-6))',
            borderColor: 'var(--mantine-color-brand-2)',
          }}
        >
          <Stack gap="md">
            <Text c="ink.8" size="md" style={{ lineHeight: 1.65, whiteSpace: 'pre-line' }}>
              {founderNote}
            </Text>
            <Text c="ink.7" size="sm">
              {founderSignature}
            </Text>
          </Stack>
        </Card>

        <Group gap="md" justify="center" mt="xl">
          <JoinLink audience="artist">
            {/* Solid Clay, matching the homepage hero / closing artist CTAs. */}
            <Button
              component="span"
              tabIndex={-1}
              size="lg"
              variant="filled"
              color={CTA_CLAY}
              styles={{ root: { minWidth: 220 } }}
            >
              {ctaArtistLabel}
            </Button>
          </JoinLink>
          <JoinLink audience="host">
            {/* Venue CTAs are Tide teal site-wide, artist CTAs stay Clay —
                matches /our-story, the homepage, /how-it-works and
                /founding-venues. */}
            <Button
              component="span"
              tabIndex={-1}
              size="lg"
              variant="gradient"
              gradient={{ from: 'tide.7', to: 'tide.6', deg: 35 }}
              styles={{ root: { minWidth: 220 } }}
            >
              {ctaHostLabel}
            </Button>
          </JoinLink>
        </Group>
      </MarketingSection>

      <MarketingNextSteps
        title={nextTitle}
        destinations={['/community-guidelines', '/dispute-resolution', '/faq']}
      />
    </PublicShell>
  );
}
